import { ClientPeppolStatus } from 'api/v2/clients';
import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Nullable } from 'types/global.types';
import {
  IntegrationsStatusEnum,
  PeppolCurrentIntegration,
} from 'types/integrations';
import { IUser } from 'types/users.types';

export const PeppolUserSendingWarnings: FC<{
  userPeppolIntegration: Nullable<PeppolCurrentIntegration>;
  className?: string;
}> = (props) => {
  const { t } = useTranslation();
  const user = useGetCurrentCustomerData().data as IUser;
  const navigate = useCustomNavigate();

  const isUserOnPeppol = !!props.userPeppolIntegration;
  const userIntegrationStatus = props.userPeppolIntegration?.status;

  const isInactive = userIntegrationStatus === IntegrationsStatusEnum.inactive;
  const isPending = userIntegrationStatus === IntegrationsStatusEnum.pending;
  const isVerificationRequired =
    userIntegrationStatus === IntegrationsStatusEnum.verification_required;
  if (!isUserOnPeppol) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={
          <div className="flex flex-wrap justify-between gap-y-1 gap-x-4">
            <span>{t('peppol.user.not_registered.' + user?.country)}</span>
            <Button
              asDefault
              className={
                'text-[#F59E0B] text-sm font-semibold flex items-center gap-1'
              }
              onClick={() => {
                navigate('/settings/peppol');
              }}
            >
              {t('peppol.user.register')}
            </Button>
          </div>
        }
      />
    );
  }
  if (isInactive) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={t('peppol.user.inactive.' + user?.country)}
      />
    );
  }
  if (isPending) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={t('peppol.user.pending.' + user?.country)}
      />
    );
  }
  if (isVerificationRequired) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={
          <div className="flex flex-wrap justify-between gap-y-1 gap-x-4">
            <span>
              {t('peppol.user.verification_required.' + user?.country)}
            </span>
            <Button
              asDefault
              className={
                'text-[#F59E0B] text-sm font-semibold flex items-center gap-1'
              }
              onClick={() => {
                navigate('/settings/peppol');
              }}
            >
              {t('peppol.user.verification_required.cta')}
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <Alert
      type="success"
      size="sm"
      className={classNames('mb-0', props.className)}
      title={t('peppol.user.registered.' + user?.country, {
        id: props.userPeppolIntegration?.metaData.identifier,
      })}
    />
  );
};

export const PeppolClientSendingWarnings: FC<{
  clientPeppolIntegration: Nullable<ClientPeppolStatus>;
  clientName: Nullable<string>;
  className?: string;
}> = (props) => {
  const { t } = useTranslation();
  const user = useGetCurrentCustomerData().data as IUser;

  const integration = props.clientPeppolIntegration;

  if (!integration) return null;

  const isClientOnPeppol = integration.exists;
  const canClientReceiveInvoices = integration.isReceivingOptionEnabled;
  const isClientCountrySupported = integration.isSupportedCountry;

  if (!isClientCountrySupported) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={t('send_invoice.peppol.client.country_not_supported', {
          clientName: props.clientName,
        })}
      />
    );
  }

  if (!isClientOnPeppol) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={t('peppol.client.not_registered.' + user?.country, {
          clientName: props.clientName,
        })}
      />
    );
  }
  if (!canClientReceiveInvoices) {
    return (
      <Alert
        type="warning"
        size="sm"
        className={classNames('mb-0', props.className)}
        title={t(
          'send_invoice.peppol.client.not_enabled.invoice.' + user?.country,
          {
            clientName: props.clientName,
          },
        )}
      />
    );
  }
  return (
    <Alert
      type="success"
      size="sm"
      className={classNames('mb-0', props.className)}
      title={t('peppol.client.registerd.' + user?.country, {
        clientName: props.clientName,
      })}
    />
  );
};
